import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import Sound from "../../../Assets/final new sound/correct-2-46134.mp3";
import { server_url } from "../../../Config/API";
import PostHooks from "../../../Hooks/PostHooks";
import UpdateHooks from "../../../Hooks/UpdateHooks";
import AuthUser from "../../../Hooks/authUser";
import { getUserHook } from "../../../Hooks/getUserHook";
import GameStatsIndicator from "../GameStatsIndicator";

const RedBlueRainDrop = () => {
  const [paddlePosition, setPaddlePosition] = useState(
    window.innerWidth / 2 - 80 // Center the 160px (w-40) paddle
  );
  const [raindrops, setRaindrops] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const navigate = useNavigate();
  const [isFullScreen, setIsFullScreen] = useState(false);

  const touchAudioRef = useRef(null);
  if (!touchAudioRef.current && typeof window !== "undefined") {
    touchAudioRef.current = new Audio(Sound);
  }

  const isUpdating = useRef(false);
  const raindropSpeed = 2;
  const raindropInterval = 2000;

  const [timeLeft, setTimeLeft] = useState(300);
  const [score, setScore] = useState(0);
  const [rainDropData, setRainDropData] = useState([]);

  const { userInfo } = AuthUser();
  const date = useRef(moment().format("YYYY-MM-D")).current;
  const time = useRef(moment().format("h:mm a")).current;

  const previousStateRef = useRef(null);
  const scoreRef = useRef(score);
  const timeRef = useRef(timeLeft);
  const paddlePosRef = useRef(paddlePosition);
  const animationFrameIdRef = useRef(null);

  scoreRef.current = score;
  timeRef.current = timeLeft;
  paddlePosRef.current = paddlePosition;

  // Red blue settings
  const [user, setUser] = useState([]);
  const [blue, setBlue] = useState(100);
  const [red, setRed] = useState(100);

  useEffect(() => {
    getUserHook(userInfo?._id, setUser);
  }, []);

  useEffect(() => {
    if (user) {
      setRed(user?.redOpacity ?? 100);
      setBlue(user?.blueOpacity ?? 100);
    }
  }, [user]);

  const fetchFunction = async () => {
    try {
      const response = await fetch(
        `${server_url}/rainDrop/specific?fieldName=userId&fieldValue=${
          userInfo?._id
        }&fieldName2=date&fieldValue2=${date}&fieldName3=gameName&fieldValue3=Rain+Drop+Binocular`
      );
      const data = await response.json();
      setRainDropData(data?.data || []);
      if (data?.data && data?.data.length) {
        setScore(data?.data[0]?.score);
        previousStateRef.current = data?.data;
      }
    } catch (error) {
      console.error("Error fetching red-blue raindrop data:", error);
    }
  };

  useEffect(() => {
    if (rainDropData?.length) {
      setTimeLeft(rainDropData[0]?.remainingTime);
      setScore(rainDropData[0]?.score);
    }
  }, [rainDropData]);

  useEffect(() => {
    fetchFunction();
  }, []);

  const insertFunction = useCallback(() => {
    if (previousStateRef?.current?.length) {
      UpdateHooks(
        `${server_url}/rainDrop/updateRainDrop/${previousStateRef?.current[0]?._id}`,
        {
          date,
          time,
          score: scoreRef.current,
          remainingTime: timeRef.current,
        }
      );
    } else {
      PostHooks(
        `${server_url}/rainDrop/addRainDrop`,
        {
          gameName: "Rain Drop Binocular",
          userId: userInfo?._id,
          userName: userInfo?.name,
          date,
          time,
          score: scoreRef.current,
          remainingTime: timeRef.current,
        },
        "Rain Drop score updated !"
      );
    }
  }, [userInfo, date, time]);

  const insertFunctionRef = useRef(insertFunction);
  useEffect(() => {
    insertFunctionRef.current = insertFunction;
  }, [insertFunction]);

  const toggleFullScreenRef = useRef(null);
  const toggleFullScreen = () => {
    if (!isFullScreen) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch((error) => {
          console.error("Error entering full screen:", error);
        });
      }
    }
    setIsFullScreen(!isFullScreen);
  };
  toggleFullScreenRef.current = toggleFullScreen;

  useEffect(() => {
    toggleFullScreen();

    const handleMouseMove = (event) => {
      const newPaddleX = event.clientX - 80;
      setPaddlePosition(newPaddleX);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const countScore = () => {
    if (!isUpdating.current) {
      isUpdating.current = true;
      setScore((prevScore) => prevScore + 1);
      setTimeout(() => {
        isUpdating.current = false;
      }, 0);
    }
  };

  const playSound = () => {
    if (touchAudioRef.current) {
      touchAudioRef.current.currentTime = 0;
      touchAudioRef.current.play().catch(e => console.log(e));
    }
  };

  const checkCollision = () => {
    const pLeft = paddlePosRef.current;
    const pRight = pLeft + 160;
    const pTop = window.innerHeight - 48; // bottom-8 is 32px + paddle height 16px
    const pBottom = window.innerHeight - 32;

    setRaindrops((prevRaindrops) => {
      let hitDetected = false;
      const filtered = prevRaindrops.filter((raindrop) => {
        const rTop = raindrop.top;
        const rBottom = raindrop.top + 40;
        const rLeft = raindrop.left;
        const rRight = raindrop.left + 20;

        const isColliding =
          rBottom >= pTop &&
          rTop <= pBottom &&
          rRight >= pLeft &&
          rLeft <= pRight;

        if (isColliding) {
          hitDetected = true;
          return false;
        }
        return true;
      });

      if (hitDetected) {
        countScore();
        playSound();
      }
      return filtered;
    });
  };

  const animateRaindrops = () => {
    setRaindrops((prevRaindrops) =>
      prevRaindrops
        .map((raindrop) => ({
          ...raindrop,
          top: raindrop.top + raindropSpeed,
        }))
        .filter((raindrop) => raindrop.top <= window.innerHeight)
    );

    checkCollision();

    if (!gameOver) {
      animationFrameIdRef.current = requestAnimationFrame(animateRaindrops);
    }
  };

  useEffect(() => {
    if (timeLeft <= 0) {
      insertFunctionRef.current();
      navigate(-1);
      setGameOver(true);
    }
  }, [timeLeft, navigate]);

  useEffect(() => {
    const countdownInterval = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    const raindropCreationInterval = setInterval(() => {
      if (!gameOver) {
        const startX = Math.random() * (window.innerWidth - 20);
        const color = Math.random() < 0.5 ? "red" : "blue";
        setRaindrops((prev) => [
          ...prev,
          { id: Math.random(), top: -40, left: startX, color },
        ]);
      }
    }, raindropInterval);

    animationFrameIdRef.current = requestAnimationFrame(animateRaindrops);

    return () => {
      clearInterval(countdownInterval);
      clearInterval(raindropCreationInterval);
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
    };
  }, [gameOver]);

  useEffect(() => {
    const handleKeyPress = async (event) => {
      if (event.keyCode === 27 && isFullScreen) {
        insertFunctionRef.current();
        await navigate(-1);
        if (toggleFullScreenRef.current) toggleFullScreenRef.current();
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [isFullScreen, navigate]);

  const min = Math.floor(timeLeft / 60);
  return (
    <div>
      <div
        id="game-container"
        className="inset-0 fixed bg-black w-screen h-screen overflow-hidden"
      >
        <div
          id="paddle"
          className="absolute w-40 h-4 bg-black z-50 bottom-8"
          style={{ left: `${paddlePosition}px` }}
        ></div>
        <div className="absolute w-full h-4 bg-white bottom-8"></div>
        <GameStatsIndicator score={score} timeLeft={timeLeft} />
        {raindrops.map((raindrop) => (
          <div
            key={raindrop.id}
            className="raindrop"
            style={{
              opacity: (raindrop.color === "red" ? red : blue) / 100,
              position: "absolute",
              width: "20px",
              height: "40px",
              backgroundColor: raindrop.color,
              borderRadius: "0%",
              left: `${raindrop.left}px`,
              top: `${raindrop.top}px`,
            }}
          ></div>
        ))}
        {gameOver && (
          <div
            id="game-over"
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-2xl text-red"
          >
            Game Over
          </div>
        )}
      </div>
    </div>
  );
};

export default RedBlueRainDrop;
